/**
 * Owns the issue-detail modal: which issue is open, its full record, and the
 * fetch that gets it.
 *
 * Its own mixin because TWO unrelated screens open the same modal — the Jira tab
 * (from a table row or a board card) and a support ticket's detail page (from
 * its linked issue). Neither should own the other's state, and the modal should
 * not be rebuilt twice.
 *
 * The detail is FETCHED rather than taken from the row that was clicked: the
 * list is deliberately slim (see `mapIssueListItem` in clockout-service), so the
 * row has no priority, no resolution date, no fine-grained stage and no linked
 * tickets. The ticket page has even less — a link carries only key, summary and
 * status. One request on open is what makes both entry points show the same
 * thing.
 *
 * Expects `issueDetailKey`, `issueDetail`, `issueDetailLoading` and
 * `issueDetailError` in the host's initial state, and a `cleanup()` that sets
 * `_unmounted` — the guards below are what keep a slow response off a screen
 * that has gone.
 */
class IssueDetailController {
    /**
     * Open the modal for an issue, or close it if that issue is already open.
     *
     * The toggle is why the key is set before the fetch: the modal opens
     * immediately in a loading state instead of waiting for the response, so a
     * click always does something visible.
     *
     * @param {string} key
     */
    async openIssueDetail(key) {
        if (!key) return;

        if (this.state.issueDetailKey === key) {
            this.closeIssueDetail();
            return;
        }

        const generation = (this._issueDetailGeneration || 0) + 1;
        this._issueDetailGeneration = generation;

        this.setState({
            issueDetailKey: key,
            issueDetail: null,
            issueDetailLoading: true,
            issueDetailError: false,
        });

        try {
            const issue = await this.data.fetchJiraIssue(key);
            // Opening a second issue while the first is in flight must not let
            // the first one's response land in the open modal.
            if (this._issueDetailGeneration !== generation || this._unmounted) return;
            this.setState({ issueDetail: issue, issueDetailLoading: false });
        } catch (error) {
            if (this._issueDetailGeneration !== generation || this._unmounted) return;
            console.error('Error loading Jira issue detail:', error);
            this.setState({ issueDetailError: true, issueDetailLoading: false });
        }
    }

    /** The modal's X, mask click and Esc all land here. */
    closeIssueDetail() {
        // Bumping the generation drops any response still in flight, so a slow
        // fetch cannot re-open a modal the user has just dismissed.
        this._issueDetailGeneration = (this._issueDetailGeneration || 0) + 1;
        this.setState({
            issueDetailKey: null,
            issueDetail: null,
            issueDetailLoading: false,
            issueDetailError: false,
        });
    }
}

export default IssueDetailController;
